import { SiSpotify, SiApplemusic, SiTidal, SiYoutube } from "react-icons/si";
import { Music2 } from "lucide-react";
import { SERVICE_LAYOUT } from "@/lib/data";
import { FadeUp, RevealLine } from "./AnimatedSection";
import { useLanguage } from "@/context/LanguageContext";

const VIDEO_PLATFORMS = [
  { key: "spotify", name: "Spotify", Icon: SiSpotify, color: "#1ED760" },
  { key: "apple-music", name: "Apple Music", Icon: SiApplemusic, color: "#FA243C" },
  { key: "tidal", name: "TIDAL", Icon: SiTidal, color: "#00FFFF" },
  { key: "boomplay", name: "Boomplay", Icon: Music2, color: "#7C4DFF" },
  { key: "xite", name: "XITE", Icon: Music2, color: "#FF9900" },
  { key: "youtube", name: "YouTube", Icon: SiYoutube, color: "#FF0000" },
  { key: "vevo", name: "VEVO", Icon: Music2, color: "#2E5EFF" },
];

export const Services = () => {
  const { t } = useLanguage();

  return (
    <section id="servicios" data-testid="services-section" className="relative py-28 md:py-36 bg-[#0A0A0A]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="mb-16 md:mb-20 max-w-2xl">
          <p className="text-brand-red text-xs font-mono tracking-[0.25em] font-semibold uppercase mb-4">{t.services.chapterLabel}</p>
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-white tracking-tight">
            <RevealLine text={t.services.titleLines[0]} />
            <RevealLine text={t.services.titleLines[1]} delay={0.08} />
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-5 auto-rows-[220px]">
          {SERVICE_LAYOUT.map((service, i) => {
            const content = t.services.items[service.id];
            return (
              <FadeUp
                key={service.id}
                delay={(i % 4) * 0.06}
                data-testid={`service-card-${service.id}`}
                className={`relative rounded-2xl border border-white/10 overflow-hidden group hover:border-white/25 hover:-translate-y-1 transition-all duration-400 ${service.span || ""}`}
              >
                {service.image && (
                  <>
                    <img src={service.image} alt={content.title} className="absolute inset-0 w-full h-full object-cover opacity-25 group-hover:opacity-35 transition-opacity duration-500" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-black/20" />
                  </>
                )}
                {!service.image && <div className="absolute inset-0 bg-white/[0.03]" />}
                <div className="relative z-10 h-full flex flex-col justify-end p-6">
                  <h3 className="font-heading font-bold text-white text-lg mb-1.5 leading-snug">{content.title}</h3>
                  <p className="text-neutral-400 text-sm leading-relaxed">{content.desc}</p>
                  {service.id === "vevo" && (
                    <div data-testid="video-platforms-strip" className="flex items-center -space-x-2 mt-4">
                      {VIDEO_PLATFORMS.map(({ key, name, Icon, color }) => (
                        <div
                          key={key}
                          title={name}
                          data-testid={`video-platform-icon-${key}`}
                          className="w-8 h-8 rounded-full border-2 border-[#0A0A0A] bg-[#141414] flex items-center justify-center shrink-0 hover:-translate-y-1 hover:z-10 transition-transform duration-200"
                          style={{ "--brand-color": color }}
                        >
                          <Icon className="w-3.5 h-3.5 text-[color:var(--brand-color)]" />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </FadeUp>
            );
          })}
        </div>
      </div>
    </section>
  );
};
